<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogBody,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogTrigger,
    } from '@/components/ui/dialog';
    import { computed, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import FileUploadZone from '../FileUploadZone.vue';
    import CreateButton from '@/components/CreateButton.vue';
    import { Checkbox } from '@/components/ui/form';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import { toast } from 'vue-sonner';

    const open = ref(false);
    const consent = ref(false);
    const uploadedFile = ref([]);

    const page = usePage<SharedData>();
    const selectedCompanyName = computed(() => page.props.auth.user.selected_company_name);
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const { t } = useI18n();

    const queryClient = useQueryClient();

    const handleClose = () => {
        uploadedFile.value = [];
        open.value = false;
    };

    const downloadTemplate = () => {
        window.open(route('v2.salary.employees.import-template'), '_blank');
    };
</script>

<template>
    <Dialog v-model:open="open">
        <DialogTrigger as-child>
            <CreateButton button-text="employee.upload.admin_header" />
        </DialogTrigger>
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>
                    {{ t('employee.upload.admin_header') }}
                </DialogTitle>
            </DialogHeader>

            <DialogBody class="space-y-5">
                <Checkbox
                    id="consent"
                    v-model="consent"
                >
                    <i18n-t
                        keypath="employee.upload.consent"
                        tag="span"
                        scope="global"
                    >
                        <template #name>
                            <span class="font-bold">
                                {{ selectedCompanyName }}
                            </span>
                        </template>
                    </i18n-t>
                </Checkbox>

                <FileUploadZone
                    v-if="consent"
                    v-model="uploadedFile"
                    :accepts-files="['.xls', '.xlsx']"
                    instant-upload
                    :route="route('v2.salary.employees.admin-import')"
                    :max-uploads="1"
                    @success="() => { toast.success(t('forms.general.success')); queryClient.invalidateQueries({ queryKey: [QueryKey.EMPLOYEES, selectedCompanyId] }); handleClose(); }"
                />
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <Button
                    variant="link"
                    width="narrow"
                    size="sm"
                    @click="downloadTemplate"
                    class="mr-auto"
                >
                    {{ t('forms.download_template') }}
                </Button>
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="handleClose"
                    >
                        {{ t('forms.close') }}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
